// ── Shell Casing Ejection System ──
// InstancedMesh-based shell casing pool for minimal draw calls.
// Spawns brass-colored cylinder casings from the weapon ejection port
// with physics simulation (gravity, bounce, spin) and auto-recycle.

import * as THREE from 'three';
import { GRAVITY } from '../core/config';
import { EFFECT_LAYER } from './effect-loop';
import { beginFloorLookupFrame, floorYAtCached } from '../gameplay/floor-lookup';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Maximum active casings in the pool (1 draw call via InstancedMesh). */
export const CASING_POOL_SIZE = 50;

/** How long each casing lives before fading and recycling (ms). */
export const CASING_LIFETIME_MS = 3000;

/** Fade begins this many ms before expiry. */
const FADE_DURATION_MS = 500;

/** Bounce restitution coefficient — energy retained after ground hit. */
const BOUNCE_RESTITUTION = 0.3;

/** Friction applied on bounce (horizontal velocity multiplier). */
const BOUNCE_FRICTION = 0.5;

// ---------------------------------------------------------------------------
// Per-weapon-class casing sizes
// ---------------------------------------------------------------------------

export type WeaponClass = 'pistol' | 'rifle' | 'sniper' | 'melee' | 'smg' | 'shotgun';

export interface CasingSize {
  radius: number;
  height: number;
}

/** Casing dimensions per weapon class. */
export const CASING_SIZES: Record<string, CasingSize> = {
  pistol: { radius: 0.003, height: 0.012 },
  smg:    { radius: 0.003, height: 0.012 },  // same as pistol
  rifle:  { radius: 0.004, height: 0.018 },
  sniper: { radius: 0.004, height: 0.018 },  // same as rifle
  shotgun:{ radius: 0.006, height: 0.025 },
};

// ---------------------------------------------------------------------------
// Internal casing slot
// ---------------------------------------------------------------------------

interface CasingSlot {
  active: boolean;
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  rotation: THREE.Euler;
  spin: THREE.Vector3;
  spawnTime: number;
  scale: number;
  bounced: boolean;
  settled: boolean;
}

// ---------------------------------------------------------------------------
// Shared geometry — cylinder (brass casing shape)
// ---------------------------------------------------------------------------

// Use a single CylinderGeometry for the InstancedMesh.
// Actual per-class size differences are applied via the instance matrix scale.
const CASING_GEOMETRY = new THREE.CylinderGeometry(1, 1, 1, 6, 1);

const CASING_MATERIAL = new THREE.MeshStandardMaterial({
  color: 0xC8A832,  // brass gold
  metalness: 0.8,
  roughness: 0.3,
});

// Temp objects for matrix composition (avoid per-frame allocations)
const _matrix = new THREE.Matrix4();
const _position = new THREE.Vector3();
const _quaternion = new THREE.Quaternion();
const _scale = new THREE.Vector3();
const _euler = new THREE.Euler();

// ---------------------------------------------------------------------------
// ShellCasingSystem
// ---------------------------------------------------------------------------

export class ShellCasingSystem {
  private mesh: THREE.InstancedMesh;
  private slots: CasingSlot[] = [];
  private scene: THREE.Scene;
  private lastUpdateTime = 0;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.mesh = new THREE.InstancedMesh(CASING_GEOMETRY, CASING_MATERIAL, CASING_POOL_SIZE);
    this.mesh.count = 0;
    this.mesh.frustumCulled = false; // small objects, always render
    this.mesh.layers.set(EFFECT_LAYER);
    this.mesh.userData.isEffect = true;
    scene.add(this.mesh);

    // Pre-allocate slot objects
    for (let i = 0; i < CASING_POOL_SIZE; i++) {
      this.slots.push({
        active: false,
        position: new THREE.Vector3(),
        velocity: new THREE.Vector3(),
        rotation: new THREE.Euler(),
        spin: new THREE.Vector3(),
        spawnTime: 0,
        scale: 1,
        bounced: false,
        settled: false,
      });
    }
  }

  /** Number of currently active casings. */
  get activeCount(): number {
    let count = 0;
    for (const slot of this.slots) {
      if (slot.active) count++;
    }
    return count;
  }

  /** Access the underlying InstancedMesh (for testing / scene queries). */
  getInstancedMesh(): THREE.InstancedMesh {
    return this.mesh;
  }

  /**
   * Spawn a shell casing ejected from a weapon fire event.
   * @param position  — world position of the ejection port
   * @param direction — forward direction the weapon is facing
   * @param weaponClass — weapon type (determines casing size)
   */
  spawn(
    position: THREE.Vector3,
    direction: THREE.Vector3,
    weaponClass: WeaponClass,
  ): void {
    // Melee weapons don't eject casings
    if (weaponClass === 'melee') return;

    // Find an inactive slot, or recycle the oldest active one
    let slot = this._findInactiveSlot();
    if (!slot) {
      slot = this._recycleOldest();
    }
    if (!slot) return; // should never happen with pre-allocated pool

    const size = CASING_SIZES[weaponClass] ?? CASING_SIZES.rifle;

    // Compute ejection direction: right + up + slight forward
    const right = new THREE.Vector3()
      .crossVectors(new THREE.Vector3(0, 1, 0), direction)
      .normalize();
    const up = new THREE.Vector3(0, 1, 0);

    slot.active = true;
    slot.position.copy(position);
    slot.spawnTime = performance.now();
    slot.scale = 1;
    slot.bounced = false;
    slot.settled = false;

    // Initial velocity: eject to the right and upward with some forward
    slot.velocity.copy(right).multiplyScalar(2 + Math.random() * 2);
    slot.velocity.addScaledVector(up, 2 + Math.random() * 1.5);
    slot.velocity.addScaledVector(direction, -0.3 + Math.random() * 0.8);

    // Random spin
    slot.spin.set(
      (Math.random() - 0.5) * 30,
      (Math.random() - 0.5) * 30,
      (Math.random() - 0.5) * 30,
    );

    slot.rotation.set(
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2,
    );

    // Update the instance matrix for this slot
    this._updateSlotMatrix(this.slots.indexOf(slot), size);
    this._syncMeshCount();
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  /**
   * Update all active casings. Call once per frame.
   * @param now — current timestamp (performance.now())
   */
  update(now: number): void {
    const dt = this.lastUpdateTime > 0
      ? Math.min((now - this.lastUpdateTime) / 1000, 0.05) // cap at 50ms
      : 0.016; // first frame default
    this.lastUpdateTime = now;

    // R-M-001: Reset the per-frame floor-Y cache so clustered casings hit
    // the cache for repeated XZ buckets within this update tick.
    beginFloorLookupFrame();

    let anyActive = false;

    for (let i = 0; i < this.slots.length; i++) {
      const slot = this.slots[i];
      if (!slot.active) continue;

      const elapsed = now - slot.spawnTime;

      // Expired — deactivate
      if (elapsed >= CASING_LIFETIME_MS) {
        slot.active = false;
        continue;
      }

      anyActive = true;

      // Physics: gravity
      if (!slot.settled) {
        slot.velocity.y += GRAVITY * dt;
        slot.position.addScaledVector(slot.velocity, dt);

        // Spin
        slot.rotation.x += slot.spin.x * dt;
        slot.rotation.y += slot.spin.y * dt;
        slot.rotation.z += slot.spin.z * dt;

        // Ground bounce — R-M-001: look up the floor Y for THIS casing's XZ
        // so brass settles on catwalks, drainage channel, etc. instead of
        // falling through to world Y=0. fromY = position.y + 0.5 ensures
        // the downward ray sees only surfaces at or below the casing (not a
        // catwalk overhead).
        const floorY = floorYAtCached(slot.position.x, slot.position.z, {
          fromY: slot.position.y + 0.5,
        });
        if (slot.position.y <= floorY) {
          slot.position.y = floorY;

          if (!slot.bounced) {
            // First bounce — partial restitution
            slot.velocity.y = Math.abs(slot.velocity.y) * BOUNCE_RESTITUTION;
            slot.velocity.x *= BOUNCE_FRICTION;
            slot.velocity.z *= BOUNCE_FRICTION;
            slot.spin.multiplyScalar(0.3);
            slot.bounced = true;
          } else {
            // Second hit — settle
            slot.velocity.set(0, 0, 0);
            slot.spin.set(0, 0, 0);
            slot.settled = true;
          }
        }
      }

      // Fade near end of life
      const fadeStart = CASING_LIFETIME_MS - FADE_DURATION_MS;
      if (elapsed > fadeStart) {
        slot.scale = 1 - (elapsed - fadeStart) / FADE_DURATION_MS;
      }

      // Update instance matrix
      // Use rifle as default size — the actual size difference is small
      // and we use one shared geometry for the InstancedMesh
      this._updateSlotMatrix(i);
    }

    if (anyActive || this.mesh.count > 0) {
      this._syncMeshCount();
      this.mesh.instanceMatrix.needsUpdate = true;
    }
  }

  /** Clean up resources. */
  dispose(): void {
    this.scene.remove(this.mesh);
    this.mesh.dispose();
    // Note: geometry and material are module-level singletons, don't dispose
  }

  // ---- Private helpers ----------------------------------------------------

  private _findInactiveSlot(): CasingSlot | null {
    for (const slot of this.slots) {
      if (!slot.active) return slot;
    }
    return null;
  }

  private _recycleOldest(): CasingSlot | null {
    let oldest: CasingSlot | null = null;
    let oldestTime = Infinity;
    for (const slot of this.slots) {
      if (slot.active && slot.spawnTime < oldestTime) {
        oldestTime = slot.spawnTime;
        oldest = slot;
      }
    }
    if (oldest) {
      oldest.active = false;
    }
    return oldest;
  }

  /**
   * Write the transform for slot `index` into the InstancedMesh buffer.
   */
  private _updateSlotMatrix(index: number, size?: CasingSize): void {
    const slot = this.slots[index];
    const s = size ?? CASING_SIZES.rifle;

    _position.copy(slot.position);
    _euler.copy(slot.rotation);
    _quaternion.setFromEuler(_euler);
    _scale.set(
      s.radius * 2 * slot.scale,
      s.height * slot.scale,
      s.radius * 2 * slot.scale,
    );

    _matrix.compose(_position, _quaternion, _scale);
    this.mesh.setMatrixAt(index, _matrix);
  }

  /**
   * Compact active casings to the front of the InstancedMesh
   * and set mesh.count to match.
   */
  private _syncMeshCount(): void {
    // InstancedMesh renders indices 0..count-1.
    // We need to pack active casings contiguously.
    let writeIdx = 0;
    for (let i = 0; i < this.slots.length; i++) {
      if (this.slots[i].active) {
        if (i !== writeIdx) {
          // Swap slot data
          const tmp = this.slots[writeIdx];
          this.slots[writeIdx] = this.slots[i];
          this.slots[i] = tmp;
          // BUG-26: Update matrix for BOTH slots after swap to prevent position mismatch
          this._updateSlotMatrix(i);
        }
        this._updateSlotMatrix(writeIdx);
        writeIdx++;
      }
    }
    this.mesh.count = writeIdx;
  }
}
